// The key this browser proves itself with (ADR 0003 point 15, RFC 9449).
//
// The session token is useless without a proof, and a proof can only be made
// with this key's private half — which is generated `extractable: false`, so
// script on the page can use it while the page is open and can never take it
// away. The identity server learns the key's thumbprint at sign-in and binds
// the token to it.
import { useIdentityKeyStore } from '../adapters/ports.js';

const KEY_ID = 'aac.proof-key.v1';
const ALGORITHM = Object.freeze({ name: 'ECDSA', namedCurve: 'P-256' });
const SIGNATURE = Object.freeze({ name: 'ECDSA', hash: 'SHA-256' });

// One key per page load, resolved once: a burst of first requests must not
// generate two keys and register only one of them.
let loading = null;

function base64Url(bytes) {
    let binary = '';
    for (const byte of new Uint8Array(bytes)) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlJson(value) {
    return base64Url(new TextEncoder().encode(JSON.stringify(value)));
}

async function sha256(text) {
    return base64Url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)));
}

async function loadKey() {
    const store = useIdentityKeyStore();
    let keyPair = await store.get(KEY_ID);
    if (!keyPair) {
        keyPair = await crypto.subtle.generateKey(ALGORITHM, false, ['sign']);
        await store.set(KEY_ID, keyPair);
    }
    const exported = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
    const publicJwk = { kty: exported.kty, crv: exported.crv, x: exported.x, y: exported.y };
    // RFC 7638: the required members, in lexicographic order, no whitespace.
    const thumbprint = await sha256(JSON.stringify({ crv: publicJwk.crv, kty: publicJwk.kty, x: publicJwk.x, y: publicJwk.y }));
    return { privateKey: keyPair.privateKey, publicJwk, thumbprint };
}

function proofKey() {
    if (loading === null) {
        loading = loadKey().catch((error) => {
            loading = null;
            throw error;
        });
    }
    return loading;
}

/** The thumbprint the identity server binds a session to (`dpop_jkt`, then `cnf.jkt`). */
export async function getKeyThumbprint() {
    return (await proofKey()).thumbprint;
}

/**
 * A fresh proof for one request. `url` may be relative (a dev proxy's `/api/…`);
 * it is resolved against the page's origin, and its query and fragment are
 * dropped, because that is what the server compares. `accessToken` is given
 * when the proof accompanies one.
 */
export async function createProof({ method, url, accessToken }) {
    const key = await proofKey();
    const target = new URL(url, globalThis.location?.origin);
    const claims = {
        jti: crypto.randomUUID(),
        htm: String(method).toUpperCase(),
        htu: `${target.origin}${target.pathname}`,
        iat: Math.floor(Date.now() / 1000)
    };
    if (accessToken) {
        claims.ath = await sha256(accessToken);
    }
    const signingInput = `${base64UrlJson({ typ: 'dpop+jwt', alg: 'ES256', jwk: key.publicJwk })}.${base64UrlJson(claims)}`;
    const signature = await crypto.subtle.sign(SIGNATURE, key.privateKey, new TextEncoder().encode(signingInput));
    return `${signingInput}.${base64Url(signature)}`;
}

/** Forget the key held in memory, so the next use reads the store again. For tests. */
export function resetProofKey() {
    loading = null;
}

export const proofKeys = { getKeyThumbprint, createProof, resetProofKey };
export default proofKeys;
